"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";

/**
 * "Your progress is saved — make it yours."
 *
 * Shown to a guest who has actually played something and is about to leave the page. It appears on
 * the way out rather than the way in, because interrupting someone before they have played anything
 * is just a toll gate; once there is a game worth keeping, the offer is genuinely useful.
 *
 * Nothing is lost either way: the game is already stored against this browser. Signing up moves it
 * onto an account so it survives a new device or a cleared browser.
 */
export function GuestProgressPrompt({ hasProgress }: { hasProgress: boolean }) {
  const t = useTranslations("guest");
  const [open, setOpen] = useState(false);
  const dismissed = useRef(false);

  useEffect(() => {
    if (!hasProgress || dismissed.current) return;

    /** Leaving via a link inside the app: intercept once, show the offer, then let them go. */
    const onClick = (event: MouseEvent): void => {
      if (dismissed.current) return;
      const link = (event.target as HTMLElement | null)?.closest?.("a");
      if (!(link instanceof HTMLAnchorElement)) return;
      // Only our own pages, and only a plain left click.
      if (link.target === "_blank" || event.metaKey || event.ctrlKey || event.button !== 0) return;
      if (new URL(link.href, window.location.href).origin !== window.location.origin) return;

      event.preventDefault();
      dismissed.current = true;
      setOpen(true);
      // Remember where they were going, so "continue" actually continues.
      pending.current = link.href;
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [hasProgress]);

  const pending = useRef<string | null>(null);

  const leave = (): void => {
    setOpen(false);
    if (pending.current) window.location.href = pending.current;
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("title")}
      data-testid="guest-prompt"
      style={backdrop}
    >
      <div style={panel}>
        <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.15rem" }}>{t("title")}</h2>
        <p style={{ margin: "0 0 1rem", color: "var(--muted)" }}>{t("body")}</p>

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Link href="/register" data-testid="guest-prompt-register" style={primary}>
            {t("createAccount")}
          </Link>
          <button type="button" data-testid="guest-prompt-continue" onClick={leave} style={secondary}>
            {t("notNow")}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  display: "grid",
  placeItems: "center",
  padding: "1rem",
  background: "rgba(0,0,0,0.45)",
  zIndex: 50,
} as const;

const panel = {
  maxWidth: "26rem",
  width: "100%",
  padding: "1.25rem",
  borderRadius: "0.6rem",
  background: "var(--bg)",
  border: "1px solid rgba(128,128,128,0.4)",
} as const;

const primary = {
  padding: "0.65rem 1rem",
  borderRadius: "0.45rem",
  background: "var(--accent)",
  color: "#fff",
  textDecoration: "none",
  fontWeight: 600,
} as const;

const secondary = {
  padding: "0.65rem 1rem",
  borderRadius: "0.45rem",
  border: "1px solid rgba(128,128,128,0.5)",
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
} as const;
